import React, { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import ReportEditor, { ReportEditorRef } from "./ReportEditor";
import { showDialogConfermaModifica } from "../../../../utils/dialog/showDialogFunction";
import { createOrUpdateReportFile, fetchExportDocxById, createReportFile } from "../../../../api/functionApi";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useAxiosInterceptor } from "../../../../api/wrappers";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  jsonContent?: any;
  reportId: string;
  reportStatus?: string;
  /** Se true, "Scarica Word" scarica direttamente senza chiedere il nome
   *  cliente (usato per Conduct Risk dove non serve). */
  skipClientNamePrompt?: boolean;
}

const ReportEditorModal = ({ isOpen, onClose, jsonContent, reportId, reportStatus, skipClientNamePrompt = false }: Props) => {
 // console.log(jsonContent + " questo è il json content nel modal");
  
  useAxiosInterceptor();
  const editorRef = useRef<ReportEditorRef | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [currentContent, setCurrentContent] = useState(jsonContent);
  const [editorKey, setEditorKey] = useState(0);

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    setCurrentContent(jsonContent);
  }, [jsonContent]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        event.target instanceof Node &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!isOpen) return null;

  // ✅ Nuova funzione per rigenerare il report
  const handleRegenerateReport = async () => {
    const result = await Swal.fire({
      title: "Rigenerare il report?",
      text: "Questa operazione potrebbe richiedere alcuni minuti. Il contenuto attuale verrà sostituito.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sì, rigenera",
      cancelButtonText: "Annulla",
      confirmButtonColor: "#374151",
      cancelButtonColor: "#9CA3AF",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: "Rigenerazione in corso...",
      text: "Attendere il completamento.",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const newFile = await createReportFile(reportId);
      
      if (newFile && newFile.json_content) {
        // Aggiorna il contenuto con il nuovo file generato
        setCurrentContent(newFile.json_content);
        setEditorKey(prev => prev + 1); // Forza re-render dell'editor
      }

      Swal.close();

      Swal.fire({
        title: "Completato!",
        text: "Il report è stato rigenerato correttamente.",
        icon: "success",
        confirmButtonColor: "#374151",
      });

    } catch (error: any) {
      Swal.close();
      Swal.fire({
        title: "Errore",
        text: error.message || "Impossibile rigenerare il report.",
        icon: "error",
      });
    }
  };

  const handleEdit = async () => {
    showDialogConfermaModifica(() => setIsEditing(true));
  };

  const handleSave = async () => {
    const html = editorRef.current?.getHTML();
    const json = editorRef.current?.getJSON();

    if (!html || !json) {
      Swal.fire("⚠️", "Editor non pronto.", "error");
      return;
    }

    Swal.fire({
      title: "Salvataggio in corso...",
      text: "Attendere prego",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      if (!reportId) throw new Error("ID del report non definito.");
      await createOrUpdateReportFile(reportId, json);

      Swal.close();
      Swal.fire({
        icon: "success",
        title: "Modifica salvata!",
        text: "Le modifiche sono state salvate correttamente.",
        confirmButtonColor: "#374151",
      });

      setCurrentContent(json);
      setIsEditing(false);
      setEditorKey(prev => prev + 1);
    } catch (error: any) {
      Swal.close();
      Swal.fire("⚠️", error.message || "Errore durante il salvataggio.", "error");
    }
  };

  const handleDownloadPDF = async () => {
    const container = document.getElementById("report-pdf-container");
    if (!container) {
      Swal.fire("⚠️", "Report non pronto per il download.", "error");
      return;
    }

    try {
      setIsDownloading(true);
      const pdf = new jsPDF("p", "mm", "a4");
      const pages = Array.from(container.children) as HTMLElement[];

      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];
        const canvas = await html2canvas(page, { scale: 1.5, useCORS: true });

        const imgBlob: Blob = await new Promise((resolve) =>
          canvas.toBlob((blob) => resolve(blob as Blob))
        );
        const imgData = URL.createObjectURL(imgBlob);

        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);

        URL.revokeObjectURL(imgData);
      }

      pdf.save("report.pdf");
    } catch (error: any) {
      Swal.fire("⚠️", error.message || "Errore durante la generazione del PDF.", "error");
    } finally {
      setIsDownloading(false);
      setIsDropdownOpen(false);
    }
  };

const handleDownloadWord = async () => {
  if (!reportId) {
    Swal.fire("⚠️", "ID report non definito.", "error");
    return;
  }

  let clientName: string | undefined;
  if (!skipClientNamePrompt) {
    // Mostra prompt per inserire il client_name (Best Execution).
    const result = await Swal.fire({
      title: "Inserisci nome cliente",
      input: "text",
      inputLabel: "Client Name",
      inputPlaceholder: "Inserisci il nome cliente",
      showCancelButton: true,
      confirmButtonText: "Scarica",
      cancelButtonText: "Annulla",
      inputValidator: (value) => {
        if (!value) return "Devi inserire un nome cliente!";
      },
      confirmButtonColor: "#374151",
      cancelButtonColor: "#9CA3AF",
    });
    if (!result.value) return;
    clientName = result.value;
  }

  Swal.fire({
    title: "Download in corso...",
    text: "Attendere prego",
    allowOutsideClick: false,
    didOpen: () => Swal.showLoading(),
  });

  try {
    setIsDownloading(true);

    // Passa il client_name se richiesto (BE), altrimenti download diretto (CR).
    const blob = await fetchExportDocxById(reportId, clientName);

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "report.docx";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    Swal.close();
    Swal.fire({
      icon: "success",
      title: "Download completato!",
      text: "Il file Word è stato scaricato correttamente.",
      confirmButtonColor: "#374151",
    });
  } catch (error: any) {
    Swal.close();
    Swal.fire("⚠️", error.message || "Errore durante il download del file Word.", "error");
  } finally {
    setIsDownloading(false);
    setIsDropdownOpen(false);
  }
};


  const handleCancel = () => {
    Swal.fire({
      title: "Sei sicuro di voler annullare?",
      text: "I dati non salvati andranno persi.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sì, annulla",
      cancelButtonText: "No, torna indietro",
      confirmButtonColor: "#374151",
      cancelButtonColor: "#9CA3AF",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        setIsEditing(false);
        setEditorKey((prev) => prev + 1);
        Swal.fire({
          icon: "info",
          title: "Modifiche annullate",
          text: "Le modifiche non salvate sono state scartate.",
          confirmButtonColor: "#374151",
        });
      }
    });
  };

  const handleClose = () => {
    setIsEditing(false);
    setEditorKey(prev => prev + 1);
    onClose();
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-lg w-[80%] max-w-[1500px] h-[90vh] p-6 z-10 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 pr-8">
          <h2 className="text-xl font-semibold">Report Editor</h2>
          <div className="flex gap-2 items-center relative" ref={dropdownRef}>
{!isEditing ? (
  <>
    {["drafted", "approved"].includes(reportStatus || "") && (
      <>
        <button
          onClick={handleRegenerateReport}
          className="px-4 py-2 bg-textPrimary text-white hover:bg-textSecondary rounded"
        >
          Rigenera Report
        </button>

        <button
          onClick={handleEdit}
          className="px-4 py-2 bg-textPrimary text-white hover:bg-textSecondary rounded"
        >
          Modifica
        </button>
      </>
    )}

    {/* ✅ Sempre visibile */}
    <div className="relative">
      <button
        onClick={handleDownloadWord}
        className="px-4 py-2 bg-textPrimary text-white hover:bg-textSecondary rounded"
      >
        📝 Scarica Word
      </button>
    </div>
  </>
) : (
  <>
    <button
      onClick={handleSave}
      className="px-4 py-2 bg-textPrimary text-white hover:bg-textSecondary rounded"
    >
      Salva
    </button>
    <button
      onClick={handleCancel}
      className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500"
    >
      Annulla
    </button>
  </>
)}

          </div>
        </div>

        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 font-bold text-lg"
        >
          ×
        </button>

        {/* Editor / Viewer */}
        <div className="flex-1 overflow-auto hide-scrollbar">
          {!currentContent ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-12 h-12 border-[6px] border-gray-300 border-t-textPrimary rounded-full animate-spin" />
            </div>
          ) : (
            <ReportEditor
              key={editorKey}
              ref={editorRef}
              editable={isEditing}
              initialJSON={currentContent}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportEditorModal;